import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import DashboardLayout from "../../layouts/DashboardLayout";
import localeText from "../../../../locale";
import { formatDate } from "../../../../utilities/formatDate";
import { formatPrice } from "../../../../utilities/formatPrice";
import UserEditService from "../../../../service/UserEditService";
import useCurrency from "../../../../hooks/useCurrency";
import "../../pages/FinancialHub/components/FinancialTransactionsTable/FinancialTransactionsTable.css";

export const typeTransfer = {
  PAYPAL: "PAYPAL",
  BANK: "BANK",
};

const userEditService = new UserEditService();

const STATUS_COMPLETED = "COMPLETED";
const STATUS_PENDING = "PENDING";
const STATUS_PROCESSING = "PROCESSING";

function getAccountDisplay(request) {
  if (request.typeWithdraw === typeTransfer.PAYPAL && request.paypalEmailToTransfer) {
    return request.paypalEmailToTransfer;
  }
  if (request.typeWithdraw === typeTransfer.BANK) {
    const parts = [];
    if (request.accountOwner) parts.push(request.accountOwner);
    if (request.bankname) parts.push(request.bankname);
    if (request.iban) parts.push(request.iban);
    return parts.length ? parts.join(" · ") : "—";
  }
  return "—";
}

function getStatusKey(request) {
  if (request.status === STATUS_COMPLETED) return "completed";
  if (request.status === STATUS_PROCESSING || request.status === STATUS_PENDING) return "pending";
  return "pending";
}

const WithdrawIntent = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await userEditService.getAllUserWithdrawRequests(
          localStorage.getItem("__extraconomy_token"),
          localStorage.getItem("__extraconomy_username"),
        );
        if (cancelled) return;
        if (res.messageCode === 200000 || res.messageCode === 200001) {
          setRequests(res.result || []);
        } else {
          setRequests([]);
        }
      } catch {
        if (!cancelled) setRequests([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredRequests = useMemo(() => {
    let list = [...requests];
    if (filterStatus === "completed") {
      list = list.filter((r) => r.status === STATUS_COMPLETED);
    } else if (filterStatus === "pending") {
      list = list.filter((r) => r.status === STATUS_PROCESSING || r.status === STATUS_PENDING);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter(
        (r) =>
          String(r.id ?? "")
            .toLowerCase()
            .includes(q) || getAccountDisplay(r).toLowerCase().includes(q),
      );
    }
    return list;
  }, [requests, filterStatus, searchText]);

  const statusConfig = {
    completed: { text: "completed", color: "#46a748" },
    pending: { text: "pending", color: "#F7B005" },
  };

  const { getSymbol } = useCurrency();
  const currencySymbol = (r) => getSymbol(r.currency || "EUR");

  return (
    <DashboardLayout>
      <nav className="flex align-items-center gap-2 mb-2 text-sm text-600" aria-label="Breadcrumb">
        <Link to="/dashboard-financeiro" className="text-600 hover:text-primary">
          {localeText("financial")}
        </Link>
        <span>/</span>
        <Link to="/dashboard-financeiro/carteira" className="text-600 hover:text-primary">
          {localeText("wallet")}
        </Link>
        <span>/</span>
        <span style={{ color: "var(--theme-text, #344563)" }}>{localeText("withdrawals")}</span>
      </nav>

      <div className="mb-3">
        <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text, #171717)" }}>
          {localeText("withdrawalRequestsPageTitle")}
        </h2>
        <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
          {localeText("withdrawalRequestsSubtitle")}
        </p>
      </div>

      <div className="financial-transactions-table">
        <div className="flex flex-wrap align-items-center gap-2 mb-4">
          <div
            className="flex gap-1 flex-wrap p-1"
            style={{
              backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
              borderRadius: "0.625rem",
            }}
          >
            <Button
              size="small"
              label={localeText("all")}
              style={{ color: "var(--theme-text-muted, #A3A3A3)" }}
              className={`p-button-text border-round-pill font-medium ${filterStatus === "all" ? "filter-pill-active" : ""} p-no-outline`}
              onClick={() => setFilterStatus("all")}
            />
            <Button
              size="small"
              label={localeText("completed")}
              style={{ color: "var(--theme-text-muted, #A3A3A3)" }}
              className={`p-button-text border-round-pill font-medium ${filterStatus === "completed" ? "filter-pill-active" : ""} p-no-outline`}
              onClick={() => setFilterStatus("completed")}
            />
            <Button
              size="small"
              label={localeText("pending")}
              style={{ color: "var(--theme-text-muted, #A3A3A3)" }}
              className={`p-button-text border-round-pill font-medium ${filterStatus === "pending" ? "filter-pill-active" : ""} p-no-outline`}
              onClick={() => setFilterStatus("pending")}
            />
          </div>
          <span className="p-input-icon-left flex-1" style={{ minWidth: "200px" }}>
            <i className="pi pi-search" />
            <InputText
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={localeText("searchByTransaction")}
              className="w-full border-round-lg"
            />
          </span>
        </div>

        <div className="overflow-hidden">
          <DataTable
            value={filteredRequests}
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            currentPageReportTemplate=""
            emptyMessage={
              <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
                <div
                  className="border-round flex align-items-center justify-content-center"
                  style={{
                    width: "120px",
                    height: "120px",
                    backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                  }}
                >
                  <i
                    className="pi pi-inbox text-6xl"
                    style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                  />
                </div>
                <p className="m-0" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                  {localeText("noWithdrawalRequests")}
                </p>
              </div>
            }
            rowClassName="cursor-pointer"
            className="financial-transactions-datatable"
          >
            <Column
              field="id"
              header={localeText("transaction")}
              sortable
              body={(row) => <span className="font-medium">{row.id}</span>}
            />
            <Column header={localeText("account")} body={(row) => getAccountDisplay(row)} />
            <Column
              field="amountRequested"
              header={localeText("amountRequested")}
              sortable
              body={(row) => (
                <span>
                  {currencySymbol(row)} {formatPrice(row.amountRequested ?? 0)}
                </span>
              )}
            />
            <Column
              field="fee"
              header={localeText("fee")}
              body={(row) => (
                <span>
                  {currencySymbol(row)} {formatPrice(row.fee ?? 0)}
                </span>
              )}
            />
            <Column
              field="amountToDeposit"
              header={localeText("amountToTransfer")}
              sortable
              body={(row) => (
                <span className="font-semibold">
                  {currencySymbol(row)} {formatPrice(row.amountToDeposit ?? 0)}
                </span>
              )}
            />
            <Column
              header={localeText("status")}
              body={(row) => {
                const key = getStatusKey(row);
                const cfg = statusConfig[key] || statusConfig.pending;
                return (
                  <span className="flex align-items-center gap-2">
                    <span
                      className="border-circle"
                      style={{
                        width: "8px",
                        height: "8px",
                        backgroundColor: cfg.color,
                      }}
                    />
                    {localeText(cfg.text)}
                  </span>
                );
              }}
            />
            <Column
              field="dateRequest"
              header={localeText("requestDate")}
              sortable
              body={(row) => (row.dateRequest ? formatDate(row.dateRequest) : "—")}
            />
            <Column
              header=""
              body={() => <i className="pi pi-chevron-right text-400" />}
              style={{ width: "40px" }}
            />
          </DataTable>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WithdrawIntent;
